import { useState } from "react";
import { CheckCircle2, Circle, Flag, History, ListChecks } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { api, formatApiError } from "../../lib/api";
import ReportDialog from "../feedback/ReportDialog";
import { STATUS, PRIORITY, ABUSE_REASON, ABUSE_STATUS, HISTORY_LABEL, fmtDate, fmtHours } from "../../lib/labels";

const hoursBetween = (a, b) => (a && b ? (new Date(b) - new Date(a)) / 3600000 : null);

export const DatesPanel = ({ fb }) => (
  <div className="card-surface space-y-2 p-5 text-sm" data-testid="feedback-dates">
    <p className="eyebrow mb-1">Délais</p>
    <p className="flex justify-between"><span className="text-slate-500">Reçu</span><span>{fmtDate(fb.created_at)}</span></p>
    <p className="flex justify-between"><span className="text-slate-500">Pris en charge</span><span data-testid="feedback-taken-at">{fmtDate(fb.taken_at)}</span></p>
    <p className="flex justify-between"><span className="text-slate-500">Résolu</span><span data-testid="feedback-resolved-at">{fmtDate(fb.resolved_at)}</span></p>
    <p className="flex justify-between"><span className="text-slate-500">Clôturé</span><span data-testid="feedback-closed-at">{fmtDate(fb.closed_at)}</span></p>
    <p className="flex justify-between border-t pt-2 text-xs text-slate-500"><span>Délai de prise en charge</span><span>{fmtHours(hoursBetween(fb.created_at, fb.taken_at))}</span></p>
    <p className="flex justify-between text-xs text-slate-500"><span>Délai de résolution</span><span>{fmtHours(hoursBetween(fb.created_at, fb.resolved_at))}</span></p>
  </div>
);

const Field = ({ label, children }) => <label className="block text-sm"><span className="eyebrow">{label}</span><div className="mt-1.5">{children}</div></label>;

export const ProcessingPanel = ({ fb, team, can, me, onSaved }) => {
  const patch = async (body) => {
    try { const { data } = await api.patch(`/company/feedbacks/${fb.id}`, body); onSaved(data); toast.success("Feedback mis à jour"); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const canAssignOthers = can("feedback:assign");
  const members = canAssignOthers ? team || [] : (team || []).filter((m) => m.user_id === me || m.user_id === fb.assigned_to);
  return (
    <div className="card-surface space-y-4 p-5" data-testid="processing-panel">
      <Field label="Statut"><select className="field-select" value={fb.status} onChange={(e) => patch({ status: e.target.value })} disabled={!can("feedback:update")} data-testid="feedback-status-select">
        {Object.entries(STATUS).map(([k, s]) => <option key={k} value={k}>{s.label}</option>)}</select></Field>
      <Field label="Priorité"><select className="field-select" value={fb.priority} onChange={(e) => patch({ priority: e.target.value })} disabled={!can("feedback:update")} data-testid="feedback-priority-select">
        {Object.entries(PRIORITY).map(([k, s]) => <option key={k} value={k}>{s.label}</option>)}</select></Field>
      <Field label="Affecté à"><select className="field-select" value={fb.assigned_to || ""} onChange={(e) => patch({ assigned_to: e.target.value })} disabled={!can("feedback:update")} data-testid="feedback-assign-select">
        <option value="">Non affecté</option>{members.map((m) => <option key={m.user_id} value={m.user_id}>{m.name}</option>)}</select></Field>
      {!canAssignOthers && fb.assigned_to !== me && <Button size="sm" variant="outline" className="w-full" onClick={() => patch({ assigned_to: me })} data-testid="feedback-self-assign">Me l'affecter</Button>}
      {fb.assignment_history?.length > 0 && (
        <div className="border-t pt-3" data-testid="assignment-history">
          <p className="eyebrow mb-2">Affectations</p>
          <ul className="space-y-1 text-xs text-slate-600">{fb.assignment_history.slice().reverse().map((a, i) => (
            <li key={i}>{a.name ? <b>{a.name}</b> : "Désaffecté"} · par {a.by} · {fmtDate(a.at)}</li>))}</ul>
        </div>
      )}
      {fb.status_history?.length > 0 && (
        <div className="border-t pt-3" data-testid="status-history">
          <p className="eyebrow mb-2">Changements de statut</p>
          <ul className="space-y-1 text-xs text-slate-600">{fb.status_history.slice().reverse().map((h, i) => (
            <li key={i}>{STATUS[h.from]?.label || "—"} → <b>{STATUS[h.to]?.label}</b> · {h.by} · {fmtDate(h.at)}</li>))}</ul>
        </div>
      )}
    </div>
  );
};

const ActionItem = ({ a, fb, can, onSaved }) => {
  const toggle = async () => {
    try { const { data } = await api.patch(`/company/feedbacks/${fb.id}/actions/${a.id}`, { status: a.status === "realisee" ? "planifiee" : "realisee" }); onSaved(data); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const Icon = a.status === "realisee" ? CheckCircle2 : Circle;
  return (
    <li className="flex gap-3 py-3" data-testid={`action-${a.id}`}>
      <button onClick={toggle} disabled={!can("feedback:update")} className="mt-0.5 text-emerald-600 disabled:opacity-50" data-testid={`action-toggle-${a.id}`} aria-label="Basculer l'état"><Icon className="h-5 w-5" /></button>
      <div className="flex-1 text-sm">
        <p className={a.status === "realisee" ? "text-slate-500 line-through" : "text-slate-800"}>{a.description}</p>
        <p className="mt-0.5 text-xs text-slate-400">{a.owner_name ? `Responsable : ${a.owner_name} · ` : ""}{a.due_date ? `Échéance : ${fmtDate(a.due_date, false)} · ` : ""}par {a.created_by}{a.done_at ? ` · réalisée le ${fmtDate(a.done_at)}` : ""}</p>
      </div>
    </li>
  );
};

export const ActionsPanel = ({ fb, team, can, onSaved }) => {
  const [form, setForm] = useState({ description: "", owner_id: "", due_date: "" });
  const [busy, setBusy] = useState(false);
  const add = async () => {
    if (form.description.trim().length < 3) { toast.error("Décrivez la mesure corrective"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/company/feedbacks/${fb.id}/actions`, { description: form.description, owner_id: form.owner_id || null, due_date: form.due_date || null });
      onSaved(data); setForm({ description: "", owner_id: "", due_date: "" }); toast.success("Mesure corrective enregistrée");
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="card-surface p-5" data-testid="actions-panel">
      <p className="flex items-center gap-2 font-display font-semibold"><ListChecks className="h-4 w-4 text-emerald-600" />Mesures correctives</p>
      {!fb.actions.length && <p className="mt-2 text-sm text-slate-500">Aucune mesure enregistrée.</p>}
      <ul className="divide-y">{fb.actions.map((a) => <ActionItem key={a.id} a={a} fb={fb} can={can} onSaved={onSaved} />)}</ul>
      {can("feedback:update") && (
        <div className="mt-3 space-y-2 border-t pt-3">
          <Textarea rows={2} maxLength={1000} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Ex. : former l'équipe d'accueil aux horaires de pointe" data-testid="action-description-input" />
          <div className="flex flex-col gap-2 sm:flex-row">
            <select className="field-select" value={form.owner_id} onChange={(e) => setForm({ ...form, owner_id: e.target.value })} data-testid="action-owner-select">
              <option value="">Responsable (facultatif)</option>{(team || []).map((m) => <option key={m.user_id} value={m.user_id}>{m.name}</option>)}
            </select>
            <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} data-testid="action-due-input" />
            <Button onClick={add} disabled={busy} data-testid="action-add-button">Ajouter</Button>
          </div>
        </div>
      )}
    </div>
  );
};

const detailText = (h) => {
  const d = h.details || {};
  const parts = [];
  if (d.status) parts.push(`statut → ${STATUS[d.status]?.label || d.status}`);
  if (d.priority) parts.push(`priorité → ${PRIORITY[d.priority]?.label || d.priority}`);
  if ("assigned_to" in d) parts.push(d.assignee_name ? `affecté à ${d.assignee_name}` : "désaffecté");
  if ("internal" in d) parts.push(d.internal ? "note interne" : d.public ? "réponse publique" : "message à l'auteur");
  if (d.reason) parts.push(ABUSE_REASON[d.reason] || d.reason);
  if (d.description) parts.push(d.description);
  if (d.note) parts.push(d.note);
  if (d.channel) parts.push(`canal : ${d.channel}`);
  return parts.join(" · ");
};

export const HistoryPanel = ({ fb }) => (
  <div className="card-surface p-5" data-testid="history-panel">
    <p className="flex items-center gap-2 font-display font-semibold"><History className="h-4 w-4 text-slate-500" />Historique des opérations</p>
    <ol className="mt-3 space-y-3 border-l border-slate-200 pl-4">
      {fb.history.map((h) => (
        <li key={h.id} className="relative text-sm" data-testid="history-entry">
          <span className="absolute -left-[21px] top-1.5 h-2 w-2 rounded-full bg-slate-400" />
          <p className="font-medium text-slate-800">{HISTORY_LABEL[h.action] || h.action}</p>
          <p className="text-xs text-slate-500">{h.actor_name || h.actor_email} · {fmtDate(h.created_at)}{detailText(h) ? ` · ${detailText(h)}` : ""}</p>
        </li>
      ))}
    </ol>
  </div>
);

export const ReportPanel = ({ fb, can, onSaved }) => {
  const [open, setOpen] = useState(false);
  const submit = async (body) => {
    try { const { data } = await api.post(`/company/feedbacks/${fb.id}/report`, body); onSaved(data); setOpen(false); toast.success("Signalement transmis à la modération"); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const pending = fb.abuse_reports.some((r) => r.status === "pending");
  return (
    <div className="card-surface space-y-3 p-5 text-sm" data-testid="report-panel">
      <p className="eyebrow">Qualité & abus</p>
      {fb.moderation_hidden && <p className="rounded-md bg-rose-50 px-3 py-2 text-rose-700" data-testid="moderation-hidden-note">Masqué publiquement par la modération VOXA AFRIKA{fb.moderation_note ? ` : ${fb.moderation_note}` : ""}. Le dossier reste consultable et traitable.</p>}
      {fb.abuse_reports.map((r) => (
        <div key={r.id} className="rounded-md border px-3 py-2 text-xs" data-testid={`abuse-report-${r.id}`}>
          <span className={`rounded-full border px-2 py-0.5 ${ABUSE_STATUS[r.status]?.cls}`}>{ABUSE_STATUS[r.status]?.label}</span>
          <p className="mt-1.5 text-slate-600">{ABUSE_REASON[r.reason]} · {r.reporter_name} · {fmtDate(r.created_at)}</p>
          {r.decision_note && <p className="mt-1 text-slate-500">Décision : {r.decision_note}</p>}
        </div>
      ))}
      {can("feedback:update") && !pending && !fb.moderation_hidden && (
        <Button size="sm" variant="outline" className="w-full" onClick={() => setOpen(true)} data-testid="report-abuse-button"><Flag className="mr-1.5 h-3.5 w-3.5" />Signaler un abus</Button>
      )}
      {open && <ReportDialog testId="company-report" onSubmit={submit} onClose={() => setOpen(false)} />}
    </div>
  );
};
